import { useState, useEffect, useCallback } from 'react';
import {
  ListChecks,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Circle,
  ChevronDown,
  ChevronRight,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { periodDisplayName, type Period, type PeriodCloseStatus, type EntityCloseStatus } from '@/types';
import { periodsApi, closeStatusApi } from '@/api/client';
import { useAppStore, useAuthStore } from '@/store';

// --- Constants ---------------------------------------------------------------

const CLOSE_STAGES = [
  'Data Collection',
  'Trial Balance Upload',
  'Account Mapping',
  'IC Matching',
  'FX Translation',
  'Equity Consolidation',
  'Tax Computation',
  'Journal Review',
  'Final Approval',
];

// --- Helpers -----------------------------------------------------------------

function stageStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case 'in_progress':
      return <Clock className="w-4 h-4 text-amber-600" />;
    default:
      return <Circle className="w-4 h-4 text-gray-300" />;
  }
}

function stageStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-500';
    case 'in_progress':
      return 'bg-amber-500';
    default:
      return 'bg-gray-200';
  }
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// --- Entity Detail Modal -----------------------------------------------------

interface EntityDetailModalProps {
  entity: EntityCloseStatus;
  periodId: string;
  onClose: () => void;
  onUpdate: () => void;
  isAdmin: boolean;
}

function EntityDetailModal({ entity, periodId, onClose, onUpdate, isAdmin }: EntityDetailModalProps) {
  const { addNotification } = useAppStore();
  const [updating, setUpdating] = useState<string | null>(null);

  const handleUpdateStage = async (stage: string) => {
    setUpdating(stage);
    try {
      await closeStatusApi.updateStage(
        Number(periodId),
        entity.entity_id,
        stage,
        { status: 'completed' },
      );
      addNotification({
        type: 'success',
        title: 'Stage Updated',
        message: `${stage} marked as completed for ${entity.entity_name}.`,
      });
      onUpdate();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: apiErr.detail || 'Failed to update stage.',
      });
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{entity.entity_name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">Close stage history</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6">
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Overall Completion</span>
              <span className="text-sm font-bold text-gray-900">{entity.completion_pct}%</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full transition-all',
                  entity.completion_pct >= 100 ? 'bg-green-500' :
                  entity.completion_pct >= 50 ? 'bg-blue-500' : 'bg-amber-500',
                )}
                style={{ width: `${Math.min(100, entity.completion_pct)}%` }}
              />
            </div>
          </div>

          {/* Stage list */}
          <div className="space-y-3">
            {entity.stages.map((stage) => (
              <div
                key={stage.stage}
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg border',
                  stage.status === 'completed' ? 'border-green-200 bg-green-50/50' :
                  stage.status === 'in_progress' ? 'border-amber-200 bg-amber-50/50' :
                  'border-gray-200 bg-gray-50/50',
                )}
              >
                <div className="flex-shrink-0">
                  {stageStatusIcon(stage.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{stage.stage}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {stage.started_at && (
                      <span className="text-xs text-gray-400">
                        Started: {formatDateTime(stage.started_at)}
                      </span>
                    )}
                    {stage.completed_at && (
                      <span className="text-xs text-gray-400">
                        Completed: {formatDateTime(stage.completed_at)}
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && stage.status !== 'completed' && (
                  <button
                    onClick={() => handleUpdateStage(stage.stage)}
                    disabled={updating === stage.stage}
                    className="btn-primary text-xs px-3 py-1.5"
                  >
                    {updating === stage.stage ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      'Complete'
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function CloseStatus() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [closeStatus, setCloseStatus] = useState<PeriodCloseStatus | null>(null);

  // UI
  const [selectedEntity, setSelectedEntity] = useState<EntityCloseStatus | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await closeStatusApi.get(Number(selectedPeriod));
      setCloseStatus(res);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load close status');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadData();
  }, [loadData, selectedPeriod]);

  const toggleExpand = (entityId: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      return next;
    });
  };

  // Stage counts
  const stageCounts: Record<string, number> = {};
  if (closeStatus?.entities) {
    CLOSE_STAGES.forEach((stage) => {
      stageCounts[stage] = closeStatus.entities.filter((e) =>
        e.stages.some((s) => s.stage === stage && s.status === 'in_progress'),
      ).length;
    });
  }

  // Skeleton loading
  if (isLoading && !closeStatus) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <ListChecks className="w-5 h-5 text-teal-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse"><div className="skeleton w-full h-12" /></div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <ListChecks className="w-5 h-5 text-teal-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Close Status</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track period close progress across entities</p>
          </div>
        </div>
        <select
          className="select-field text-sm w-40"
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
        >
          {periods.length === 0 && <option value="">No periods</option>}
          {periods.map((p) => (
            <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* No data */}
      {!closeStatus && !error ? (
        <div className="card py-16 text-center">
          <ListChecks className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No close status data</h3>
          <p className="text-sm text-gray-500 mt-1">
            Close status is tracked automatically during the consolidation process.
          </p>
        </div>
      ) : closeStatus && (
        <>
          {/* Overall Progress */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {closeStatus.period_name} - Overall Progress
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {closeStatus.entities_complete} of {closeStatus.entities_total} entities complete
                </p>
              </div>
              <span className="text-2xl font-bold text-gray-900 font-mono">
                {closeStatus.overall_pct}%
              </span>
            </div>
            <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full transition-all duration-500',
                  closeStatus.overall_pct >= 100 ? 'bg-green-500' :
                  closeStatus.overall_pct >= 50 ? 'bg-blue-500' : 'bg-amber-500',
                )}
                style={{ width: `${Math.min(100, closeStatus.overall_pct)}%` }}
              />
            </div>
          </div>

          {/* Stage Pipeline */}
          <div className="card p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Stage Pipeline</h2>
            <div className="flex items-start gap-1 overflow-x-auto pb-2">
              {CLOSE_STAGES.map((stage, idx) => {
                const count = stageCounts[stage] || 0;
                const totalComplete = closeStatus.entities.filter((e) =>
                  e.stages.some((s) => s.stage === stage && s.status === 'completed'),
                ).length;
                const total = closeStatus.entities_total;
                const pct = total > 0 ? Math.round((totalComplete / total) * 100) : 0;

                return (
                  <div key={stage} className="flex items-center">
                    <div className="flex flex-col items-center min-w-[100px]">
                      <div
                        className={clsx(
                          'w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold mb-2',
                          pct >= 100 ? 'bg-green-500 text-white' :
                          pct > 0 ? 'bg-amber-500 text-white' :
                          'bg-gray-200 text-gray-500',
                        )}
                      >
                        {pct}%
                      </div>
                      <p className="text-xs text-gray-700 text-center font-medium leading-tight">
                        {stage}
                      </p>
                      {count > 0 && (
                        <span className="badge bg-amber-50 text-amber-700 text-[10px] mt-1">
                          {count} active
                        </span>
                      )}
                    </div>
                    {idx < CLOSE_STAGES.length - 1 && (
                      <div className="w-6 h-0.5 bg-gray-200 mt-[-16px]" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Entity Table */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900">Entity Close Status</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header w-8"></th>
                    <th className="table-header">Entity</th>
                    <th className="table-header">Stage Indicators</th>
                    <th className="table-header text-right w-28">Completion</th>
                    <th className="table-header w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {closeStatus.entities.map((entity) => {
                    const isExpanded = expandedRows.has(entity.entity_id);
                    return (
                      <>
                        <tr
                          key={entity.entity_id}
                          className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                          onClick={() => toggleExpand(entity.entity_id)}
                        >
                          <td className="table-cell">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </td>
                          <td className="table-cell text-sm font-medium text-gray-800">
                            {entity.entity_name}
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center gap-1">
                              {entity.stages.map((s) => (
                                <div
                                  key={s.stage}
                                  title={`${s.stage}: ${s.status}`}
                                  className={clsx(
                                    'w-3 h-3 rounded-full',
                                    stageStatusColor(s.status),
                                  )}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="table-cell text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={clsx(
                                    'h-full rounded-full',
                                    entity.completion_pct >= 100 ? 'bg-green-500' :
                                    entity.completion_pct >= 50 ? 'bg-blue-500' : 'bg-amber-500',
                                  )}
                                  style={{ width: `${Math.min(100, entity.completion_pct)}%` }}
                                />
                              </div>
                              <span className="text-xs font-mono text-gray-700">
                                {entity.completion_pct}%
                              </span>
                            </div>
                          </td>
                          <td className="table-cell">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEntity(entity);
                              }}
                              className="text-xs font-medium text-primary-600 hover:text-primary-800 underline"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${entity.entity_id}-detail`} className="border-b border-gray-100">
                            <td colSpan={5} className="px-6 py-3 bg-gray-50/50">
                              <div className="grid grid-cols-3 gap-2">
                                {entity.stages.map((s) => (
                                  <div
                                    key={s.stage}
                                    className={clsx(
                                      'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm',
                                      s.status === 'completed' ? 'border-green-200 bg-green-50/50' :
                                      s.status === 'in_progress' ? 'border-amber-200 bg-amber-50/50' :
                                      'border-gray-200',
                                    )}
                                  >
                                    {stageStatusIcon(s.status)}
                                    <span className="text-xs text-gray-700">{s.stage}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Entity Detail Modal */}
      {selectedEntity && (
        <EntityDetailModal
          entity={selectedEntity}
          periodId={selectedPeriod}
          onClose={() => setSelectedEntity(null)}
          onUpdate={() => {
            loadData();
            setSelectedEntity(null);
          }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
